import { Helmet } from "react-helmet-async";
import { ExternalLink, Info } from "lucide-react";
import { PARTNER_LINK } from "@/lib/constants";

import heroMain from "@/assets/hero-main.webp";

const HeroSection = () => (
  <section id="start" className="relative md:min-h-[70vh] md:flex md:items-center md:overflow-hidden">
    <Helmet>
      <link
        rel="preload"
        as="image"
        href={heroMain}
        media="(min-width: 768px)"
        // @ts-expect-error: fetchpriority is valid HTML, not yet in React DOM typings for link
        fetchpriority="high"
      />
    </Helmet>

    <figure className="hidden md:block absolute inset-0">
      <img
        src={heroMain}
        alt="heilys Produkte im Überblick – unabhängige Einordnung und Partnerlink zum offiziellen Shop"
        title="heilys Partnerlink & Produktinfos – unabhängige Übersicht"
        className="h-full w-full object-cover object-center"
        loading="eager"
        fetchPriority="high"
        decoding="async"
        width={1200}
        height={800}
      />
      <figcaption className="sr-only">
        heilys Produkte im Überblick – unabhängige Einordnung zu Panels, Gen30+ Vision, AddRed und Wearables sowie der Partnerlink zum offiziellen heilys Shop.
      </figcaption>
      <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/80 to-background/40 md:via-background/70 md:to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/40" />
    </figure>

    <div className="container relative py-8 md:py-24">
      <div className="grid items-center gap-10 lg:grid-cols-2">
        <div className="max-w-xl">
          <h1 className="font-display text-2xl font-extrabold leading-tight tracking-tight text-foreground sm:text-3xl md:text-5xl">
            heilys Partnerlink &amp; Produktinfos – unabhängige Übersicht
          </h1>
          <a
            href="#update-hinweis"
            className="mt-3 inline-flex items-center gap-2 rounded-full border border-primary/50 bg-primary/20 px-3.5 py-1.5 text-xs font-semibold text-primary shadow-sm transition-all hover:bg-primary/30 hover:border-primary/70 sm:text-sm"
          >
            <Info className="h-3.5 w-3.5" aria-hidden="true" />
            Update ab 01.08.2026 – Partnerprogramm umgestellt
          </a>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:mt-4 sm:text-base md:text-lg">
            Unabhängige Orientierung zur Marke heilys: Welche Geräte es gibt, wofür sie sich eignen und
            worauf du bei Wellenlängen, Bauform und Anwendung achten solltest. Für den Kauf verlinken wir
            direkt zum offiziellen heilys Shop.
          </p>

          <div className="mt-6 md:mt-8">
            <div className="flex flex-col flex-wrap items-start gap-3 sm:flex-row sm:items-center">
              <a
                href={PARTNER_LINK}
                target="_blank"
                rel="sponsored noopener noreferrer"
                onClick={() => window.lh?.trackAffiliate({ source: "hero_primary" })}
                className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/25 transition-all hover:shadow-xl hover:shadow-primary/30 hover:brightness-110 w-full sm:w-auto"
              >
                Zum offiziellen heilys Shop
                <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
              </a>
              <a
                href="#produktwelten"
                className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-border bg-card/80 px-6 py-3 text-sm font-semibold text-foreground transition-colors hover:bg-secondary w-full sm:w-auto"
              >
                heilys Produkte ansehen
              </a>
            </div>
            <p className="mt-3 sm:mt-4 text-[11px] text-muted-foreground">
              Unabhängige Informationsseite mit transparentem Partnerhinweis. Der Preis bleibt für dich unverändert.
            </p>
          </div>
        </div>

        <div className="hidden lg:block" />
      </div>

      <figure className="md:hidden mt-8 overflow-hidden rounded-xl border border-border">
        <img
          src={heroMain}
          alt="heilys Produkte im Überblick – unabhängige Einordnung und Partnerlink zum offiziellen Shop"
          className="h-auto w-full object-cover"
          loading="lazy"
          decoding="async"
          width={1200}
          height={800}
        />
      </figure>

      <nav aria-label="Schnellnavigation" className="mt-6 md:mt-10 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
        <a href="#update-hinweis" className="hover:text-foreground transition-colors">Update 2026</a>
        <span className="text-border hidden sm:inline" aria-hidden="true">·</span>
        <a href="#partnerlink-status" className="hover:text-foreground transition-colors">Wo bestellen?</a>
        <span className="text-border hidden sm:inline" aria-hidden="true">·</span>
        <a href="#ueber-heilys" className="hover:text-foreground transition-colors">Über heilys</a>
        <span className="text-border hidden sm:inline" aria-hidden="true">·</span>
        <a href="#produktwelten" className="hover:text-foreground transition-colors">Produktwelten</a>
        <span className="text-border hidden sm:inline" aria-hidden="true">·</span>
        <a href="#produkt-spotlight" className="hover:text-foreground transition-colors">Gen30+Vision</a>
        <span className="text-border hidden sm:inline" aria-hidden="true">·</span>
        <a href="#addred" className="hover:text-foreground transition-colors">AddRed</a>
        <span className="text-border hidden sm:inline" aria-hidden="true">·</span>
        <a href="#partnerlink-info" className="hover:text-foreground transition-colors">Partnerlink &amp; Produktinfos</a>
        <span className="text-border hidden sm:inline" aria-hidden="true">·</span>
        <a href="#partnerlink-support" className="hover:text-foreground transition-colors">Partnerlink</a>
        <span className="text-border hidden sm:inline" aria-hidden="true">·</span>
        <a href="#faq" className="hover:text-foreground transition-colors">Häufige Fragen</a>
      </nav>
    </div>
  </section>
);

export default HeroSection;
